// Opening rendering utilities (doors, windows, openings)

/** @typedef {import('../types/jsdoc-types.js').Opening} Opening */
/** @typedef {import('../types/jsdoc-types.js').Point} Point */

import { gridPointToPixel } from './coordinate-transforms.js';
import { drawDoorIcon, drawWindowIcon } from './icon-rendering.js';

const DOOR_COLOR = '#ff8800'; // Orange
const WINDOW_COLOR = '#00FFFF'; // Cyan
const OPENING_COLOR = '#FFFFFF'; // White

/**
 * Draw an opening line segment
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array<Point>} points - Pixel coordinates
 * @param {string} color
 * @param {number} zoom
 * @param {boolean} [isDashed=false]
 * @param {boolean} [showCircles=false]
 */
export function drawOpeningSegment(ctx, points, color, zoom, isDashed = false, showCircles = false) {
  if (points.length < 2) return;

  ctx.strokeStyle = color;
  ctx.lineWidth = 6 / zoom;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  if (isDashed) {
    ctx.setLineDash([10 / zoom, 10 / zoom]);
  } else {
    ctx.setLineDash([]);
  }

  const first = points[0];
  if (!first) return;
  ctx.beginPath();
  ctx.moveTo(first.x, first.y);
  for (let i = 1; i < points.length; i++) {
    const pt = points[i];
    if (!pt) continue;
    ctx.lineTo(pt.x, pt.y);
  }
  ctx.stroke();

  // Draw control points as circles when selected/hovered
  if (showCircles) {
    ctx.fillStyle = color;
    const circleRadius = 8 / zoom;
    for (const point of points) {
      ctx.beginPath();
      ctx.arc(point.x, point.y, circleRadius, 0, 2 * Math.PI);
      ctx.fill();
    }
  }

  ctx.setLineDash([]);
}

/**
 * Draw all openings with selection/hover highlighting
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array<Opening>} openings
 * @param {object} options
 * @param {number} options.gridSize
 * @param {number} options.zoom
 * @param {string} options.tool - Current editor tool
 * @param {boolean} options.showWalls - Whether walls layer is visible
 * @param {number|null} [options.selectedOpeningIndex=null]
 * @param {number|null} [options.hoveredOpeningIndex=null]
 * @param {{start: {x: number, y: number}, end: {x: number, y: number}}|null} [options.hoveredWallSegment=null]
 * @param {string} [options.openingSubtool='selectOpening']
 */
export function drawOpenings(ctx, openings, options) {
  const {
    gridSize,
    zoom,
    tool,
    showWalls,
    selectedOpeningIndex = null,
    hoveredOpeningIndex = null,
    hoveredWallSegment = null,
    openingSubtool = 'selectOpening',
  } = options;

  const isOpeningTool = tool === 'drawOpening';

  // Show openings when opening tool is active or walls toggle is on
  const shouldShowOpenings = isOpeningTool || showWalls;

  for (let i = 0; i < openings.length; i++) {
    const opening = openings[i];
    if (!opening) continue;

    // Use points array (backend format), skip if not available
    if (!opening.points || opening.points.length < 2) continue;
    if (opening.isComplete === false) continue;

    // Convert grid coordinates to pixels
    const pixelPoints = opening.points.map((gp) => gridPointToPixel(gp, gridSize));
    const isSelected = i === selectedOpeningIndex;
    const isHovered = i === hoveredOpeningIndex;

    // Determine color based on properties
    /** @type {string} */
    let color;
    if (opening.blocksMovement && opening.blocksVision) {
      // Door-like (blocks both)
      color = DOOR_COLOR;
    } else if (opening.blocksMovement && !opening.blocksVision) {
      // Window-like (blocks movement only)
      color = WINDOW_COLOR;
    } else {
      // Opening-like (blocks neither)
      color = OPENING_COLOR;
    }

    // Determine rendering style based on tool and selection state
    const showCircles = isOpeningTool && (isSelected || isHovered);
    let alpha;

    if (isOpeningTool) {
      if (isSelected || isHovered) {
        alpha = 1.0;
      } else {
        alpha = 0.5;
      }
    } else {
      alpha = 0.5;
    }

    // Dashed when door is open
    const isDashed = opening.type === 'door' && opening.isOpen;

    // Draw opening line segment only if should be shown
    if (shouldShowOpenings) {
      ctx.globalAlpha = alpha;
      drawOpeningSegment(ctx, pixelPoints, color, zoom, isDashed, showCircles);
      ctx.globalAlpha = 1.0;
    }

    // Draw icon at midpoint based on type (always visible regardless of toggle)
    const firstPoint = pixelPoints[0];
    const lastPoint = pixelPoints[pixelPoints.length - 1];
    if (!firstPoint || !lastPoint) continue;
    const midX = (firstPoint.x + lastPoint.x) / 2;
    const midY = (firstPoint.y + lastPoint.y) / 2;

    if (opening.type === 'door') {
      drawDoorIcon(ctx, midX, midY, gridSize, zoom, opening.isOpen || false, isSelected);
    } else if (opening.type === 'window') {
      drawWindowIcon(ctx, midX, midY, gridSize, zoom);
    }
    // No icon for 'opening' type
  }

  // Draw wall segment preview when hovering (matches Vue's drawWallSegmentPreview)
  if (isOpeningTool && hoveredWallSegment && hoveredOpeningIndex === null) {
    /** @type {string} */
    let previewColor;
    if (openingSubtool === 'drawDoor') {
      previewColor = DOOR_COLOR;
    } else if (openingSubtool === 'drawWindow') {
      previewColor = WINDOW_COLOR;
    } else if (openingSubtool === 'drawOpening') {
      previewColor = OPENING_COLOR;
    } else {
      return; // No preview for select mode
    }

    const seg = hoveredWallSegment;

    // Black outline
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 8 / zoom;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(seg.start.x, seg.start.y);
    ctx.lineTo(seg.end.x, seg.end.y);
    ctx.stroke();

    // Colored solid line on top
    ctx.strokeStyle = previewColor;
    ctx.lineWidth = 6 / zoom;
    ctx.beginPath();
    ctx.moveTo(seg.start.x, seg.start.y);
    ctx.lineTo(seg.end.x, seg.end.y);
    ctx.stroke();

    // Endpoint circles
    const circleRadius = 8 / zoom;
    for (const point of [seg.start, seg.end]) {
      ctx.fillStyle = previewColor;
      ctx.beginPath();
      ctx.arc(point.x, point.y, circleRadius, 0, 2 * Math.PI);
      ctx.fill();

      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2 / zoom;
      ctx.stroke();
    }
  }
}
